import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { LogOut } from "lucide-react";
import { toast } from "sonner";

const toLocalInput = (d) => {
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

// Floor checkout: record who takes a finished part from the rack so pickups are traceable.
export default function CheckOutButton({ rework, onDone, className = "" }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [by, setBy] = useState("");
  const [at, setAt] = useState(toLocalInput(new Date()));
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  if (rework.status !== "Completed - Finished Rack" || user?.role === "management") return null;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/reworks/${rework.id}/collect`, {
        picked_up_by: by, picked_up_at: new Date(at).toISOString(), note,
      });
      toast.success(`${rework.job_number} / ${rework.part_number} checked out by ${by}`);
      setOpen(false);
      onDone?.(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not check out");
    } finally {
      setBusy(false);
    }
  };

  const field = "rounded-sm border-[#cfcfc9] bg-white text-[#232826] focus-visible:ring-[#647265]";
  return (
    <>
      <Button disabled={busy} onClick={() => setOpen(true)} data-testid={`checkout-${rework.id}`}
        className={`h-8 rounded-sm bg-[#424e43] hover:bg-[#232826] text-[#f7f5f1] text-[11px] uppercase tracking-widest px-3 ${className}`}>
        <LogOut size={12} className="mr-1.5" /> Check out
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white border-[#e6e5df] rounded-sm" data-testid="checkout-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-[#232826]">Check out of rack</DialogTitle>
            <DialogDescription className="text-[#6b6a68]">
              <span className="mono font-semibold text-[#232826]">{rework.job_number} / {rework.part_number}</span> — record who is taking this part from the rack.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4 pt-1">
            <div className="space-y-1.5">
              <label className="text-[10px] uppercase tracking-widest text-[#6b6a68]" htmlFor="checkout-by">Picked up by</label>
              <Input id="checkout-by" data-testid="checkout-by" required autoFocus value={by} onChange={(e) => setBy(e.target.value)} placeholder="e.g. Sam — assembly" className={field} />
            </div>
            <div className="space-y-1.5">
              <label className="text-[10px] uppercase tracking-widest text-[#6b6a68]" htmlFor="checkout-at">Date & time</label>
              <Input id="checkout-at" data-testid="checkout-at" type="datetime-local" required value={at} onChange={(e) => setAt(e.target.value)} className={field} />
            </div>
            <div className="space-y-1.5">
              <label className="text-[10px] uppercase tracking-widest text-[#6b6a68]" htmlFor="checkout-note">Note (optional)</label>
              <Textarea id="checkout-note" data-testid="checkout-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Where it's going, anything useful" className={field} />
            </div>
            <Button type="submit" disabled={busy || !by.trim()} data-testid="checkout-submit"
              className="w-full h-11 rounded-sm bg-[#424e43] hover:bg-[#232826] text-[#f7f5f1] text-xs uppercase tracking-widest font-semibold">
              <LogOut size={14} className="mr-2" /> {busy ? "Saving…" : "Confirm check out"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
